import { Big } from 'big.js';
import type { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData.ts';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { OrderType } from '@/feature/order_entry/order_types.ts';
import type { ViewPosition } from '@/feature/terminal_tables/position/types.ts';
import { bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';

/**
 * Single home for turning an order form's size into executable units. `OrderValidator` and
 * the order packers (`apiOrderParams`/`onChainOrderParams`) must resolve sizes identically, so all consume these helpers.
 */

/**
 * Price for converting a quote-denominated size to asset units: mark for market orders (a
 * limit-price value lingering from a previous order type is deliberately ignored), trigger
 * price for stop-markets, limit price otherwise — falling back to mark while fields are empty.
 */
export function resolveSizingPrice(formData: OrderFormData, marketData: MarketData): Big | undefined {
  const stopPrice = 'stopPrice' in formData && formData.stopPrice?.trim() ? formData.stopPrice : undefined;
  const limitPrice = 'price' in formData && formData.price?.trim() ? formData.price : undefined;

  let fieldPrice: string | undefined;
  if (formData.orderType === OrderType.market) {
    fieldPrice = undefined;
  } else if (formData.orderType === OrderType.stopMarket) {
    fieldPrice = stopPrice;
  } else {
    fieldPrice = limitPrice ?? stopPrice;
  }

  const value = fieldPrice ?? (marketData.markPrice != null ? String(marketData.markPrice) : undefined);
  if (value == null) return undefined;

  const price = stringToBig(value);
  return price.gt(0) ? price : undefined;
}

export interface ResolvedOrderSize {
  assetSize: Big;
  /** Fixed-point units at the market's assetDecimals — what actually ships. */
  atomicSize: bigint;
}

/** Convert the form's size (asset- or quote-denominated) to asset units at `sizingPrice`. */
export function resolveOrderSize(
  formData: Pick<OrderFormData, 'size' | 'sizeUnit'>,
  sizingPrice: Big | undefined,
  assetDecimals: number
): ResolvedOrderSize {
  const raw = formData.size?.trim() ? stringToBig(formData.size) : new Big(0);
  const assetSize = formData.sizeUnit === 'quote' ? (sizingPrice?.gt(0) ? raw.div(sizingPrice) : new Big(0)) : raw;
  return { assetSize, atomicSize: bigToBigInt(assetSize, assetDecimals) };
}

export function positionSizeAtomic(position: ViewPosition, assetDecimals: number): bigint {
  return bigToBigInt(stringToBig(position.size), assetDecimals);
}

/**
 * An opposite-side order no larger than the open position — sent as a close order type even
 * without the reduce-only flag.
 */
export function canImplicitClose(
  orderSide: Side,
  position: ViewPosition | undefined,
  orderSizeAtomic: bigint,
  assetDecimals: number
): boolean {
  if (!position || position.size <= 0 || position.side === orderSide) return false;
  if (orderSizeAtomic <= 0n) return false;
  return orderSizeAtomic <= positionSizeAtomic(position, assetDecimals);
}

/**
 * Form sizes are stale snapshots — a TP/SL fill can shrink the position before submit, and the
 * backend rejects an oversized reduce-only order outright. Clamp to the live position.
 */
export function clampReduceOnlySize(
  formData: Pick<OrderFormData, 'reduceOnly' | 'side'>,
  position: ViewPosition | undefined,
  sizeAtomic: bigint,
  assetDecimals: number
): bigint {
  if (!formData.reduceOnly || !position || position.size <= 0 || position.side === formData.side) {
    return sizeAtomic;
  }
  const positionAtomic = positionSizeAtomic(position, assetDecimals);
  return sizeAtomic > positionAtomic ? positionAtomic : sizeAtomic;
}
